import {
    ComponentProps,
    ComponentPropsWithoutRef,
    ForwardedRef,
    createContext,
    forwardRef,
    useContext,
    useMemo,
} from 'react';
import { BoxIcon, BoxIconProps } from '@givebutter/primitives/box-icon';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { twMerge } from 'tailwind-merge';

interface IFauxTableEmptyContext {
    variant?: 'primary' | 'secondary';
}

const FauxTableEmptyContext = createContext<IFauxTableEmptyContext>({
    variant: 'primary',
});

interface FauxTableEmptyProps extends ComponentPropsWithoutRef<'div'>, IFauxTableEmptyContext {}
const FauxEmpty = forwardRef((props: FauxTableEmptyProps, ref: ForwardedRef<HTMLDivElement>) => {
    const { variant = 'primary', className = '', children, ...rest } = props;

    const providerProps = useMemo(
        () => ({
            variant,
        }),
        [variant],
    );

    return (
        <FauxTableEmptyContext.Provider value={providerProps}>
            <div className="flex justify-center">
                <div
                    ref={ref}
                    className={twMerge(
                        'flex max-w-sm flex-col items-center justify-center gap-2 p-12',
                        className,
                    )}
                    {...rest}
                >
                    {children}
                </div>
            </div>
        </FauxTableEmptyContext.Provider>
    );
});

interface EmptyIconProps extends Omit<BoxIconProps, 'size'> {}
const EmptyIcon = (props: EmptyIconProps) => {
    const { className = '', children, ...rest } = props;
    const { variant } = useContext(FauxTableEmptyContext);

    return (
        <BoxIcon
            size="lg"
            variant={variant === 'primary' ? 'primary' : 'secondary'}
            className={className}
            {...rest}
        >
            {!children ? <FontAwesomeIcon icon={faSearch} /> : children}
        </BoxIcon>
    );
};

interface EmptyTitleProps extends ComponentProps<'p'> {}

const EmptyTitle = (props: EmptyTitleProps) => {
    const { className = '', children, ...rest } = props;
    const { variant } = useContext(FauxTableEmptyContext);

    return (
        <p
            className={twMerge(
                'text-center font-medium text-gray-900',
                variant === 'primary' ? 'text-gray-900' : 'text-gray-600',
                className,
            )}
            {...rest}
        >
            {!children ? <>No results found</> : children}
        </p>
    );
};

interface EmptyDescriptionProps extends ComponentProps<'p'> {}

const EmptyDescription = (props: EmptyDescriptionProps) => {
    const { className = '', children = <>Try adjusting your criteria</>, ...rest } = props;

    return (
        <p className={twMerge('text-center text-sm text-gray-600', className)} {...rest}>
            {children}
        </p>
    );
};

export { FauxEmpty, EmptyIcon, EmptyTitle, EmptyDescription };
